import { ref, computed } from 'vue';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';

export function useAdvisors(websiteUrl = 'https://ratesautotransport.com') {
  const advisors = ref([]);
  const isLoading = ref(false);
  const error = ref(null);

  const fetchAdvisorsForWebsite = async () => {
    try {
      isLoading.value = true;
      error.value = null;

      // Step 1: Find the website document by URL
      const websitesRef = collection(db, 'websites');
      const websiteQuery = query(websitesRef, where('url', '==', websiteUrl));
      const websiteDocs = await getDocs(websiteQuery);

      if (websiteDocs.empty) {
        throw new Error('Not found');
      }

      const websiteId = websiteDocs.docs[0].id;

      // Step 2: Get all advisorIds linked to this website
      const advisorWebsitesRef = collection(db, 'advisor_websites');
      const advisorWebsiteQuery = query(advisorWebsitesRef, where('websiteId', '==', websiteId));
      const advisorWebsiteDocs = await getDocs(advisorWebsiteQuery);

      const advisorIds = advisorWebsiteDocs.docs.map(doc => doc.data().advisorId);

      if (advisorIds.length === 0) {
        advisors.value = [];
        return;
      }

      // Step 3: Fetch each advisor document by ID
      const advisorPromises = advisorIds.map(advisorId =>
        getDoc(doc(db, 'advisors', advisorId))
      );
      const advisorDocs = await Promise.all(advisorPromises);
      const advisorData = advisorDocs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Step 4: Collect unique tag IDs and fetch tag documents
      const tagIds = new Set();
      advisorData.forEach(advisor => {
        if (advisor.tags && Array.isArray(advisor.tags)) {
          advisor.tags.forEach(tagId => tagIds.add(tagId));
        }
      });

      const tagPromises = Array.from(tagIds).map(tagId =>
        getDoc(doc(db, 'tags', tagId))
      );
      const tagDocs = await Promise.all(tagPromises);
      const tagsMap = {};
      tagDocs.forEach(doc => {
        if (doc.exists()) {
          tagsMap[doc.id] = doc.data();
        }
      });

      // Step 5: Replace tag IDs with resolved tag objects
      advisors.value = advisorData.map(advisor => ({
        ...advisor,
        tags: advisor.tags ? advisor.tags.map(tagId => tagsMap[tagId]).filter(Boolean) : []
      }));
    } catch (err) {
      error.value = err.message;
      console.error('Error fetching advisors:', err);
    } finally {
      isLoading.value = false;
    }
  };

  return {
    advisors: computed(() => advisors.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    fetchAdvisorsForWebsite
  };
}
